import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Building2, 
  Calendar, 
  Users, 
  Layout, 
  CheckCircle,
  Shield,
  Zap,
  Globe,
  Star,
  ArrowRight
} from 'lucide-react';

const PLAN_FEATURES = [
  'Sınırsız fuar yönetimi',
  'İnteraktif kroki editörü',
  'Firma ve stant yönetimi',
  'Sözleşme oluşturma & PDF',
  'E-posta entegrasyonu',
  'Takım üyesi ekleme',
  'Raporlar ve analizler',
  '7/24 destek',
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [activePlan, setActivePlan] = useState('yearly');

  const features = [
    {
      icon: Layout,
      title: 'İnteraktif Kroki Editörü',
      description: 'Sürükle-bırak ile kolayca fuar alanı tasarlayın. Grid bazlı sistem ile metrekare hassasiyetinde çizim yapın.'
    },
    {
      icon: Building2,
      title: 'Çoklu Hol Yönetimi',
      description: 'Fuar merkezinizdeki tüm holları tek platformda yönetin. Her hol için ayrı kroki oluşturun.'
    },
    {
      icon: Users,
      title: 'Firma Talep Sistemi',
      description: 'Firmalar stant talebinde bulunur, siz onlara en uygun alanları atarsınız. Tüm süreç dijital!'
    },
    {
      icon: Calendar,
      title: 'Fuar Yönetimi',
      description: 'Birden fazla fuarı aynı anda yönetin. Tarih, konum ve organizatör bilgilerini tek yerden kontrol edin.'
    },
    {
      icon: Zap,
      title: 'Altyapı Gösterimi',
      description: 'Koridor, kapı, yangın çıkışı, su ve elektrik tesisatını kroki üzerinde işaretleyin.'
    },
    {
      icon: Shield,
      title: 'Güvenli ve Kolay',
      description: 'Rol bazlı yetkilendirme ile güvenli çalışın. Ultra password ile kritik işlemleri koruyun.'
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50 to-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Expogrid.io</h1>
                <p className="text-xs text-gray-500">Fuar Yönetim Sistemi</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                data-testid="header-login-btn"
              >
                Giriş Yap
              </Button>
              <Button
                onClick={() => navigate('/ultra-admin/login')}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="header-ultra-admin-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Ultra Admin
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Fuar Organizasyonunuzu
            <br />
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Dijitalleştirin
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
            Fuar alanı planlamasından stant atamasına kadar tüm süreçleri tek platformda yönetin.
            İnteraktif kroki editörü ile profesyonel fuar organizasyonu yapın.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/pricing')}
              className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8 py-6"
              data-testid="hero-get-started-btn"
            >
              Hemen Başlayın
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/login')}
              className="text-lg px-8 py-6"
            >
              Giriş Yap
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Güçlü Özellikler
            </h3>
            <p className="text-lg text-gray-600">
              Fuar organizasyonunuz için ihtiyacınız olan her şey
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-2 border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Nasıl Çalışır?
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">1</span>
              </div>
              <h4 className="text-xl font-semibold mb-3">Fuar Oluşturun</h4>
              <p className="text-gray-600">
                Fuar bilgilerini girin, hollerinizi tanımlayın ve ölçülerini belirleyin.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">2</span>
              </div>
              <h4 className="text-xl font-semibold mb-3">Kroki Çizin</h4>
              <p className="text-gray-600">
                İnteraktif editör ile stant alanları, koridorlar ve altyapıyı işaretleyin.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-emerald-600">3</span>
              </div>
              <h4 className="text-xl font-semibold mb-3">Stant Atayın</h4>
              <p className="text-gray-600">
                Firma taleplerini değerlendirin ve en uygun alanları tek tıkla atayın.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-white" id="pricing">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <Zap className="w-3 h-3" />
              14 Gün Ücretsiz Deneme — Kredi kartı gerekmez
            </div>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Şeffaf Fiyatlandırma</h3>
            <p className="text-gray-500 text-lg">Tek plan, tüm özellikler. Gizli ücret yok.</p>
          </div>

          {/* Plan Toggle */}
          <div className="flex justify-center mb-8">
            <div className="flex bg-gray-100 rounded-xl p-1 w-fit">
              <button
                onClick={() => setActivePlan('monthly')}
                className={`px-8 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                  activePlan === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Aylık
              </button>
              <button
                onClick={() => setActivePlan('yearly')}
                className={`px-8 py-2.5 text-sm font-semibold rounded-lg transition-all relative ${
                  activePlan === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Yıllık
                <span className="absolute -top-2.5 -right-1 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  -20%
                </span>
              </button>
            </div>
          </div>

          {/* Two Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Monthly Card */}
            <div className={`rounded-2xl border-2 overflow-hidden transition-all ${activePlan === 'monthly' ? 'border-teal-500 shadow-xl scale-[1.02]' : 'border-gray-200 shadow-sm'}`}>
              <div className={`px-6 py-6 ${activePlan === 'monthly' ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-1.5 mb-3">
                  {activePlan === 'monthly' && <Star className="w-4 h-4 fill-white" />}
                  <span className={`text-xs font-bold uppercase tracking-wide ${activePlan === 'monthly' ? 'text-white' : 'text-gray-500'}`}>Aylık Plan</span>
                </div>
                <div className="flex items-end gap-1">
                  <span className={`text-4xl font-extrabold ${activePlan === 'monthly' ? 'text-white' : 'text-gray-900'}`}>$39</span>
                  <span className={`mb-1 text-sm ${activePlan === 'monthly' ? 'text-teal-100' : 'text-gray-400'}`}>/ay</span>
                </div>
                <p className={`text-sm mt-1 ${activePlan === 'monthly' ? 'text-teal-100' : 'text-gray-400'}`}>Aylık faturalandırılır</p>
              </div>
              <div className="px-6 py-5 bg-white space-y-2.5">
                {PLAN_FEATURES.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{f}</span>
                  </div>
                ))}
                <button
                  onClick={() => navigate('/register?plan=monthly')}
                  className={`w-full flex items-center justify-center gap-2 mt-4 py-3 rounded-xl font-bold text-sm transition-all ${
                    activePlan === 'monthly'
                      ? 'bg-gray-900 hover:bg-gray-800 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  14 Gün Ücretsiz Dene <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Yearly Card */}
            <div className={`rounded-2xl border-2 overflow-hidden transition-all relative ${activePlan === 'yearly' ? 'border-teal-500 shadow-xl scale-[1.02]' : 'border-gray-200 shadow-sm'}`}>
              {activePlan === 'yearly' && (
                <div className="absolute top-3 right-3 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full z-10">
                  ÖNERİLEN
                </div>
              )}
              <div className={`px-6 py-6 ${activePlan === 'yearly' ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-1.5 mb-3">
                  {activePlan === 'yearly' && <Star className="w-4 h-4 fill-white" />}
                  <span className={`text-xs font-bold uppercase tracking-wide ${activePlan === 'yearly' ? 'text-white' : 'text-gray-500'}`}>Yıllık Plan</span>
                </div>
                <div className="flex items-end gap-2">
                  <div>
                    <div className="flex items-end gap-1">
                      <span className={`text-4xl font-extrabold ${activePlan === 'yearly' ? 'text-white' : 'text-gray-900'}`}>$31</span>
                      <span className={`mb-1 text-sm ${activePlan === 'yearly' ? 'text-teal-100' : 'text-gray-400'}`}>/ay</span>
                    </div>
                    <p className={`text-sm mt-1 ${activePlan === 'yearly' ? 'text-teal-100' : 'text-gray-400'}`}>Yıllık $375 fatura edilir</p>
                  </div>
                  <div className={`ml-auto text-xs font-bold px-2.5 py-1 rounded-full ${activePlan === 'yearly' ? 'bg-white/20 text-white' : 'bg-teal-100 text-teal-700'}`}>
                    $93 Tasarruf
                  </div>
                </div>
              </div>
              <div className="px-6 py-5 bg-white space-y-2.5">
                {PLAN_FEATURES.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{f}</span>
                  </div>
                ))}
                <button
                  onClick={() => navigate('/register?plan=yearly')}
                  className={`w-full flex items-center justify-center gap-2 mt-4 py-3 rounded-xl font-bold text-sm transition-all ${
                    activePlan === 'yearly'
                      ? 'bg-gray-900 hover:bg-gray-800 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  14 Gün Ücretsiz Dene <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-gray-400 mt-6">
            Her iki planda da tüm özellikler dahildir. İstediğiniz zaman iptal edebilirsiniz.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-bold mb-6">
            Fuar Organizasyonunuzu Bugün Modernleştirin
          </h3>
          <p className="text-xl mb-10 text-emerald-50">
            Binlerce metrekarelik alanı dakikalar içinde planlayın
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/pricing')}
            className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6"
            data-testid="cta-get-started-btn"
          >
            Ücretsiz Deneyin
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Building2 className="w-6 h-6" />
            <span className="text-xl font-bold text-white">Expogrid.io</span>
          </div>
          <p className="text-sm">
            © 2026 Expogrid.io. Tüm hakları saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
}